<template>
  <div class="navigation">
    <Button label="Zurück" outline @clicked="decreaseStep" />
    <div class="navigation__right">
      <Button
        v-if="previousAppointmentId && activeStep === 5"
        label="Abbrechen"
        outline
        @clicked="cancelEditing"
      />
      <Button :label="nextLabel" @clicked="increaseStep" :disabled="isNextButtonDisabled" />
    </div>
  </div>
</template>

<script>
import Button from '@/components/Button.vue'
import { convertToSlug } from '@/utils/format.js'
import { mapGetters } from 'vuex'

export default {
  components: { Button },
  emits: ['nextStep'],
  props: {
    nextLabel: {
      type: String,
      default: 'Weiter',
    },
    isNextButtonDisabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters([
      'activeStep',
      'stepDetails',
      'previousAppointmentId',
      'selectedServicesName',
      'selectedVehicle',
      'kfzartId',
      'activeStation',
      'selectedTimeslot',
      'currentAppointment',
    ]),
  },
  watch: {
    // renders multiple times because Navigation in other components are kept-alive
    activeStep(newValue, oldValue) {
      if (newValue === 0 && oldValue === 5) {
        this.goToNextStep()
      } else if (newValue < oldValue) {
        // can jump back by a few steps thus need dynamic oldValue
        this.updateStepDetails('previous', oldValue)
        this.goToPreviousStep()
      } else {
        this.updateStepDetails('next')
        this.goToNextStep()
      }
    },
  },
  methods: {
    decreaseStep() {
      if (this.activeStep > 0) {
        this.$store.commit('setActiveStep', this.activeStep - 1)
      }
    },
    increaseStep() {
      if (this.activeStep === 2 || this.activeStep === 4 || this.activeStep === this.stepDetails.length) {
        // has to wait for API response || is last step
        this.$emit('nextStep', this.activeStep)
      } else if (this.activeStep < this.stepDetails.length) {
        this.$emit('nextStep', this.activeStep)
        this.$store.commit('setActiveStep', this.activeStep + 1)
      }
    },
    goToPreviousStep() {
      // new value of activeStep
      switch (this.activeStep) {
        case 0: {
          this.$router.push({ name: 'Home' })
          break
        }
        case 1: {
          this.$router.push({
            name: 'Service',
            query: { bundesland: this.$route.query.bundesland },
          })
          break
        }
        case 2: {
          this.$router.push({
            name: 'Vehicle',
            params: { services: this.$route.params.services },
            query: { bundesland: this.$route.query.bundesland },
          })
          break
        }
        case 3: {
          this.$router.push({
            name: 'Station',
            params: { services: this.$route.params.services },
            query: {
              bundesland: this.$route.query.bundesland,
              ...(this.selectedVehicle && { kfzid: this.selectedVehicle.id }),
              ...(!this.selectedVehicle && { kfzartid: this.kfzartId }),
            },
          })
          break
        }
        case 4: {
          this.$router.push({
            name: 'Appointment',
            params: {
              services: this.$route.params.services,
              station: this.$route.params.station,
            },
            query: {
              bundesland: this.$route.query.bundesland,
              ...(this.selectedVehicle && { kfzid: this.selectedVehicle.id }),
              ...(!this.selectedVehicle && { kfzartid: this.kfzartId }),
            },
          })
          break
        }
      }
    },
    goToNextStep() {
      // new value of activeStep
      switch (this.activeStep) {
        case 2: {
          this.$router.push({
            name: 'Vehicle',
            params: { services: this.createServiceSlugs() },
            query: { bundesland: this.$route.query.bundesland },
          })
          break
        }
        case 3: {
          if (this.selectedVehicle) {
            this.$router.push({
              name: 'Station',
              params: { services: this.$route.params.services },
              query: {
                bundesland: this.$route.query.bundesland,
                kfzid: this.selectedVehicle.id,
              },
            })
          } else {
            this.$router.push({
              name: 'Station',
              params: { services: this.$route.params.services },
              query: {
                bundesland: this.$route.query.bundesland,
                kfzartid: this.kfzartId,
              },
            })
          }
          break
        }
        case 4: {
          this.$router.push({
            name: 'Appointment',
            params: {
              services: this.$route.params.services,
              station: this.createStationSlug(),
            },
            query: {
              bundesland: this.$route.query.bundesland,
              ...(this.selectedVehicle && { kfzid: this.selectedVehicle.id }),
              ...(!this.selectedVehicle && { kfzartid: this.kfzartId }),
            },
          })
          break
        }
        case 5: {
          this.$router.push({
            name: 'Overview',
            params: { ...this.$route.params },
            query: {
              ...this.$route.query,
              tag: this.selectedTimeslot[0].datum,
              von: this.selectedTimeslot[0].von,
            },
          })
          break
        }
        case 0: {
          this.$router.push({
            name: 'Confirmation',
            params: {
              appointmentId: this.currentAppointment.id,
            },
          })
        }
      }
    },
    updateStepDetails(type, previousStep) {
      switch (type) {
        case 'previous': {
          this.$store.commit('setStepDetailsComplete', {
            step: previousStep,
            isComplete: false,
          })
          break
        }
        case 'next': {
          this.$store.commit('setStepDetailsComplete', {
            step: this.activeStep - 1,
            isComplete: true,
          })
          break
        }
      }
    },
    createServiceSlugs() {
      return this.selectedServicesName.map((name) => convertToSlug(name)).join('+')
    },
    createStationSlug() {
      return convertToSlug(this.activeStation.bezeichnung)
    },
    cancelEditing() {
      this.$store.dispatch('deleteLockedAppointment')
      this.$router.push({ name: 'Home' })
    },
  },
}
</script>

<style lang="scss">
.navigation {
  display: flex;
  justify-content: space-between;

  @media (max-width: $breakpoint-max-mobile) {
    flex-direction: column;
    row-gap: 8px;

    .button {
      width: 100%;
    }
  }

  &__right {
    display: flex;
    gap: 8px;
  }
}
</style>
